import axios from 'axios';
import { addToQueue } from '../utils/syncQueue';
import offlineDB from '../utils/offlineDB';

const API_BASE = (() => {
  try { const u = localStorage.getItem('sgv_api_url'); if (u) return u; } catch {}
  return process.env.REACT_APP_API_URL || 'https://segavan.vercel.app';
})();

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Métodos que tienen sentido encolar offline (escrituras)
const QUEUEABLE = ['post', 'put', 'patch', 'delete'];

// Construye la clave de caché incluyendo query params
function cacheKey(config) {
  let url = config.url || '';
  if (config.params && Object.keys(config.params).length > 0) {
    url += (url.includes('?') ? '&' : '?') + new URLSearchParams(config.params).toString();
  }
  return url;
}

api.interceptors.response.use(
  (res) => {
    const data = res.data;
    // Cachear respuestas GET con JSON (no blobs)
    if (res.config.method === 'get' && !res.config.responseType) {
      offlineDB.saveResponse(cacheKey(res.config), data).catch(() => {});
    }
    return data;
  },
  async (err) => {
    const isLoginRequest = err.config?.url?.includes('/api/auth/login');
    if (err.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('token');
      localStorage.removeItem('sgv_user');
      window.location.href = '/login';
      return Promise.reject(err);
    }

    const isNetworkError = !err.response && (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || !navigator.onLine);
    const method = (err.config?.method || '').toLowerCase();

    // GET sin red → devolver caché si existe, con fallback progresivo de URL
    if (isNetworkError && method === 'get' && !err.config?.responseType) {
      try {
        const key = cacheKey(err.config);
        let cached = await offlineDB.getResponse(key);

        // Fallback 1: quitar param 'search' (búsquedas vacías o escritas en la caja)
        if (cached === null && key.includes('?')) {
          const qIdx  = key.indexOf('?');
          const path  = key.slice(0, qIdx);
          const ps    = new URLSearchParams(key.slice(qIdx + 1));
          ps.delete('search');
          const noSearch = ps.toString() ? `${path}?${ps}` : path;
          if (noSearch !== key) cached = await offlineDB.getResponse(noSearch);

          // Fallback 2: solo el path base (sin ningún query param)
          if (cached === null && noSearch !== path)
            cached = await offlineDB.getResponse(path);
        }

        if (cached !== null) return cached;
      } catch {}
    }

    // Escritura sin red → encolar para sync posterior
    if (isNetworkError && QUEUEABLE.includes(method)) {
      addToQueue(
        method.toUpperCase(),
        err.config.url,
        err.config.data ? JSON.parse(err.config.data) : undefined
      );
      return Promise.resolve({ success: true, offline: true, queued: true });
    }

    const message = err.response?.data?.error || err.message || 'Error de conexión';
    return Promise.reject(new Error(message));
  }
);

export default api;
export { API_BASE };
